import React, { useState, useRef, forwardRef, useContext } from 'react';
import { Button, Checkbox } from '@design-system';
import cn from 'classnames';
import { useOnClickOutside } from 'hooks/useClickOutside';
import { isNil } from 'lodash';
import { WLContext } from '@contexts';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import classNames from 'classnames';

export interface IModalProps {
  cancelText?: string;
  children: React.ReactNode;
  className?: string;
  closeOnClickOutside?: boolean;
  closeOnSuccess?: boolean;
  formId?: string;
  handleCancel?: () => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  onSuccess?: (v?: boolean | string) => void;
  persist?: boolean;
  setShowModal: (v: boolean) => void;
  showButtons?: boolean;
  showCancel?: boolean;
  showModal: boolean;
  subtitle?: string;
  title?: string;
  width?: string;
  acceptText?: string;
  closeOnCancel?: boolean;
  centeredSubmit?: boolean;
  isCloseable?: boolean;
}

export const Modal = forwardRef<HTMLDivElement, IModalProps>((props, ref) => {
  const { t } = useTranslation();
  const {
    cancelText = t(translations.COMMON.CANCEL),
    children,
    className,
    closeOnClickOutside = true,
    closeOnSuccess = true,
    formId,
    handleCancel = () => { },
    isDisabled = false,
    isLoading = false,
    onSuccess = () => { },
    persist = false,
    setShowModal,
    showButtons = true,
    showCancel = true,
    showModal,
    subtitle,
    title,
    width = 'normal',
    acceptText,
    closeOnCancel = true,
    centeredSubmit = false,
    isCloseable = true,
  } = props;

  const node = useRef(null);

  const [checkboxActive, setCheckboxActive] = useState(true);

  const { color } = useContext(WLContext);

  const handleSuccess = () => {
    onSuccess(checkboxActive);
    if (closeOnSuccess) setShowModal(false);
  };

  useOnClickOutside(node, () => {
    if (closeOnClickOutside) {
      handleCancel();
      setShowModal(false);
    }
  });

  const onActive = () => {
    setCheckboxActive((prev) => !prev);
  };

  const onCancel = (isFromCloseButton = false) => {
    if (handleCancel) handleCancel();
    if (setShowModal && (closeOnCancel || isFromCloseButton))
      setShowModal(false);
  };

  return (
    <div ref={ref}>
      {showModal ? (
        <>
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden p-4 outline-none focus:outline-none">
            <div
              className={cn(
                'relative mx-auto my-6 w-full',
                width === 'normal' ? 'w-full' : width,
              )}
            >
              <div
                className={cn(
                  'shadow-2xl relative flex flex-col rounded-2xl border border-white/40 bg-white/80 backdrop-blur-xl outline-none focus:outline-none',
                )}
                ref={node}
              >
                {/*header*/}
                {(!title && !subtitle) ? (
                  // Compact header: absolute on portrait, relative h-8 on landscape
                  <div className="absolute top-0 right-0 landscape:relative landscape:h-8 w-full flex justify-end z-10 pointer-events-none">
                    {isCloseable && (
                      <button
                        className="bg-transparent text-3xl font-semibold border-0 leading-none text-black opacity-50 outline-none focus:outline-none pointer-events-auto mr-4 mt-2"
                        onClick={() => onCancel(true)}
                      >
                        <span className="bg-transparent block text-subtitle text-black opacity-50 outline-none focus:outline-none">
                          ×
                        </span>
                      </button>
                    )}
                  </div>
                ) : (
                  // Standard header...
                  <div className="flex h-14 flex-row items-start justify-between rounded-t p-5 pb-0 relative">
                    {!isNil(title) && (
                      <h3 className="text-3xl m-auto mx-auto flex-row text-center text-subtitle font-bold">
                        {title}
                      </h3>
                    )}
                    {!isNil(subtitle) && (
                      <h4 className="text-3xl font-semibold flex-row text-paragraph">
                        {subtitle}
                      </h4>
                    )}

                    {isCloseable && (
                      <button
                        className="bg-transparent text-3xl font-semibold absolute right-6 top-5 border-0 leading-none text-black opacity-50 outline-none focus:outline-none"
                        onClick={() => onCancel(true)}
                      >
                        <span className="bg-transparent block text-subtitle text-black opacity-50 outline-none focus:outline-none">
                          ×
                        </span>
                      </button>
                    )}
                  </div>
                )}

                {/*body*/}
                <div
                  className={cn(
                    'relative flex-auto p-6',
                    className,
                  )}
                >
                  {children}
                </div>

                {/*footer*/}
                {(showCancel || showButtons || persist) ? (
                  <div className="flex p-6 ">
                    <div className="inline-block">
                      {showCancel && (
                        <Button
                          className="max-w mb-2 px-8 text-white"
                          variant="solid"
                          color="#C4C4C4"
                          onClick={() => onCancel()}
                          isDisabled={isLoading}
                          size="sm"
                        >
                          {cancelText}
                        </Button>
                      )}
                      {persist && (
                        <Checkbox
                          onActive={onActive}
                          defaultValue={checkboxActive}
                          label={t(translations.INPUTS.DONT_SHOW_AGAIN)}
                        />
                      )}
                    </div>
                    <div
                      className={classNames(
                        centeredSubmit
                          ? 'mx-auto inline-block'
                          : 'ml-auto inline-block',
                      )}
                    >
                      {showButtons && (
                        <Button
                          form={formId}
                          color={color}
                          className="max-w mb-2 px-8"
                          type="submit"
                          variant="primary"
                          isLoading={isLoading}
                          isDisabled={isDisabled || isLoading}
                          size="sm"
                          {...(isNil(formId)
                            ? { onClick: () => handleSuccess() }
                            : {})}
                        >
                          {acceptText
                            ? acceptText
                            : t(translations.COMMON.CONTINUE)}
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-3"></div>
                )}
              </div>
            </div>
          </div>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"></div>
        </>
      ) : null}
    </div>
  );
});

Modal.displayName = 'Modal';
